import { FC } from 'react'
import InlineSvg from 'react-inlinesvg'
import { observer } from 'mobx-react-lite'
import { PrismicRichText } from '@prismicio/react'
import { ImageField, LinkField } from '@prismicio/client'
import tw from 'twin.macro'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import { Body, Grid, Header, SmallHeader } from '../UI'
import Carousel from '../Carousel'
import PrismicLinkInternalOrExternal from '../PrismicLinkInternalOrExternal'
import { RichTextFields } from '../../types/prismic'
import config from '../../../tailwind.config'

import ctaArrow from '../../images/icons/cta-arrow.svg'
import { richTextStrongComponent } from '../../utils/richTextComponents'

type Props = {
  header: string
  subhead: RichTextFields
  clinicians: Array<{
    image: ImageField
    name: string
    copy: string
  }>
  cta_text: string
  cta_link: LinkField
  background_color?: 'gb-blue' | 'gb-red' | 'gb-magenta'
  pagePath: string
  pageTitle: string
}

type ClinicalTeamCarouselProps = {
  image: ImageField
  name: string
  copy: string
}

const ClinicalTeamCarouselComponent: FC<ClinicalTeamCarouselProps> = ({
  image,
  name,
  copy,
}) => {
  return (
    <div tw="flex flex-col mx-[10px] lg:mx-[25px] w-[100%] lg:w-[30%] relative py-7 lg:px-7 overflow-hidden relative">
      <div tw="relative w-[220px] h-[220px] lg:(w-[260px] h-[260px])">
        <GatsbyImage
          image={getImage(image)}
          alt=""
          tw="absolute w-full h-full grayscale contrast-125"
        />
        <div tw="absolute w-full h-full bg-[rgba(25,23,156,0.3)]" />
      </div>
      <p css={[SmallHeader, tw`pt-6 pb-2`]}>{name}</p>
      <div css={[Body, tw`line-height[22px]`]}>{copy}</div>
    </div>
  )
}

const ClinicalTeamSection: FC<Props> = ({
  header,
  subhead,
  clinicians,
  cta_text,
  cta_link,
  background_color = 'gb-blue',
  pagePath,
  pageTitle,
}) => {
  const bgColor = config.theme.extend.colors[background_color]
  const hoverColor = config.theme.extend.colors[`${background_color}-dark`]

  return (
    <section
      css={[
        { backgroundColor: bgColor },
        tw`pt-[40px] pb-[60px] lg:(pt-[80px] pb-[100px]) text-white font-domaine overflow-hidden relative`,
      ]}
      id="providers"
    >
      <Grid>
        {/* Headline content */}
        <div tw="col-span-full lg:col-span-9">
          {header && (
            <div tw="col-span-full lg:(col-span-5)">
              <h2 css={[Body, tw`mb-[15px] lg:mb-[25px]`]}>{header}</h2>
            </div>
          )}
          <h3 css={Header}>
            <PrismicRichText
              field={subhead.richText}
              components={richTextStrongComponent}
            />
          </h3>
        </div>
        {/* Clinicians */}
        <div tw="col-span-full mb-4">
          <Carousel
            data={clinicians}
            ItemComponent={ClinicalTeamCarouselComponent}
            initialIndexSmall={0}
            initialIndexLarge={1}
            controls="circular"
          />
        </div>
        {/* CTA */}
        <div tw="col-span-full">
          {!!cta_text && !!cta_link && (
            <PrismicLinkInternalOrExternal
              field={cta_link}
              trackEventParams={{
                pageTitle: pageTitle,
                path: pagePath,
                target: 'url' in cta_link ? cta_link.url : '',
                copy: cta_text,
              }}
              cssStyle={[
                {
                  backgroundColor: bgColor,
                  '&:hover': { backgroundColor: hoverColor },
                },
                tw`uppercase pt-2 pb-1 font-object text-[13px] line-height[13px] letter-spacing[1px] border-b border-white lg:transition-colors`,
              ]}
            >
              {cta_text}
              <InlineSvg
                src={ctaArrow}
                tw="w-6 ml-[5px] mt-[-2px] inline text-white"
              />
            </PrismicLinkInternalOrExternal>
          )}
        </div>
      </Grid>
    </section>
  )
}

export default observer(ClinicalTeamSection)
